import { cssObj } from '@/css/resources/functions.css';
import { breakpoints, fontWeights } from '@/css/resources/variables.css';
import {
	ulMarker,
	olMarker,
	resetFirstLastMargins,
	resetLastMarginBottom,
} from '@/css/resources/placeholders.css';

/**
 * Line Clamp
 * @param {Number} maxLines - Number of lines
 *
 * Example:
 * lineClamp(3)
 */
export const lineClamp = (maxLines) =>
	cssObj({
		display: '-webkit-box',
		WebkitLineClamp: maxLines,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
		/* fallback */
		// maxHeight: `calc(${lineHeight} * ${maxLines})`,
	});

/**
 * Crop line-height
 * remove the top space from your text element
 * https://medium.com/codyhouse/line-height-crop-a-simple-css-formula-to-remove-top-space-from-your-text-9c3de06d7c6f
 * @param {Number} lineHeight - unitless line-height of the text that needs cropping
 *
 * Example:
 * lhCrop(1.4)
 */
export const lhCrop = (lineHeight) =>
	cssObj({
		'&::before': {
			content: '""',
			display: 'block',
			height: 0,
			width: 0,
			marginTop: `calc((1 - ${lineHeight}) * 0.5em)`,
		},
	});

/**
 * Faux Subgrid mixin
 * @param {Number} columns - number of columns of the subgrid
 *
 * Example:
 * fauxSubgrid(4)
 */
export const fauxSubgrid = (columns) =>
	cssObj({
		gridColumnEnd: `span ${columns}`,
		display: 'grid',
		gridTemplateColumns: `repeat(${columns}, 1fr)`,
		gridColumnGap: 'inherit',
		gridRowGap: 'inherit',
	});

/**
 * CSS object-fit feature with polyfill
 * @param {Keyword} fit - object-fit value
 * @param {Keyword} position - object-position value (optional)
 *
 * Example:
 * objectFit()
 * objectFit('fit', 'relative')
 */
export const objectFit = (fit = 'fill', position = null) => {
	const posNotNull = cssObj({
		fontFamily: `object-fit: ${fit}; object-position: ${position};`,
		oObjectPosition: position,
		objectPosition: position,
	});
	const posNull = cssObj({
		fontFamily: `object-fit: ${fit};`,
	});

	return cssObj({
		...(position === null ? posNull : posNotNull),
		oObjectPosition: fit,
		objectPosition: fit,
	});
};

/**
 * CSS image-aspect-ratio feature
 * image keeps the aspect ratio according to the given width and height values
 * include mixin on the image-wrapper
 * adapted from https://css-tricks.com/aspect-ratio-boxes/, https://css-tricks.com/snippets/sass/maintain-aspect-ratio-mixin/
 * @param {Keyword} width - image width
 * @param {Keyword} height - image height
 *
 * Example:
 * imageAspectRatio(150, 200)
 */
export const imageAspectRatio = (width, height) =>
	cssObj({
		overflow: 'hidden',
		position: 'relative',

		'&::before': {
			display: 'block',
			content: '""',
			width: '100%',
			paddingTop: `calc(${height} / ${width} * 100%)`,
		},

		'& > img': cssObj(objectFit('cover'), {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			width: '100%',
			height: '100%',
		}),
	});

/**
 * To make link inherit the size of the parent
 * @param {String} element - tag or class
 * @param {String} position - pseudo-element
 *
 * Example:
 * expandLink()
 */
export const expandLink = (element = 'a', position = 'before') =>
	cssObj({
		position: 'relative',

		[`${element}`]: {
			position: 'static',
		},

		[`${element}::${position}`]: {
			content: '""',
			display: 'block',
			position: 'absolute',
			top: '0',
			right: '0',
			bottom: '0',
			left: '0',
			backgroundColor: 'transparent',
			zIndex: 1,
		},
	});

/**
 * Strip units of value
 * @param {String} number - number with unit
 *
 * Example:
 * stripUnit('200px')
 */
export const stripUnit = (number) => {
	const strippedNumber = parseFloat(number);

	return strippedNumber;
};

/**
 * Front fluid calc with css vars that doesn't require any postcss calculation
 * @param {String} property - css property
 * @param {Number} minVal - unit free minimum value (must be in px but without 'px')
 * @param {Number} maxVal - unit free minimum value (must be in px but without 'px')
 * @param {Boolean} isImportant - boolean to defined if !important should be added
 *
 * Example:
 * fluidValues('fontSize', '40px', '50px')
 */

export const fluidValues = (property, minVal, maxVal, isImportant = false) => {
	if (minVal == null && maxVal == null) {
		return null;
	} else if (minVal == maxVal || (minVal && maxVal == null)) {
		return cssObj({
			[`${property}`]: `${minVal}px ${isImportant ? '!important' : ''}`,
		});
	} else {
		return cssObj({
			[`${property}`]: `${minVal}px ${isImportant ? '!important' : ''}`,

			[`@media (min-width: ${breakpoints.fluidMin})`]: {
				[`${property}`]: `calc(
		 ${minVal}px + (${maxVal} - ${minVal}) *
			 ((100vw - ${breakpoints.fluidMin}) / (${stripUnit(
					breakpoints.fluidMax
				)} - ${stripUnit(breakpoints.fluidMin)}))
	 ) ${isImportant ? '!important' : ''}`,
			},
			[`@media (min-width: ${breakpoints.fluidMax})`]: {
				[`${property}`]: `${maxVal}px ${
					isImportant ? '!important' : ''
				}`,
			},
		});
	}
};

/**
 * Font style
 * @param {Object} font - variables of font
 *
 * Example:
 * fontStyle(styles.title)
 */
export const fontStyle = (font, withMargin = true) =>
	cssObj(
		fluidValues('fontSize', font?.fontSize?.[0], font?.fontSize?.[1]),
		fluidValues('lineHeight', font?.lineHeight?.[0], font?.lineHeight?.[1]),
		fluidValues(
			'letterSpacing',
			font?.letterSpacing?.[0],
			font?.letterSpacing?.[1]
		),
		{
			fontFamily: font?.fontFamily,
			fontWeight: font?.fontWeight,
			textTransform: font?.textTransform,
			textAlign: font?.textAlign,
			color: font?.color,
			...(withMargin &&
				cssObj({
					marginTop: font?.margin?.top,
					marginBottom: font?.margin?.bottom,
				})),
		}
	);

/**
 * Fluid Padding
 * @param {Object} padding - padding values
 *
 * Example:
 * fluidPadding(styles.padding)
 */
export const fluidPadding = (padding) =>
	cssObj(
		fluidValues('paddingTop', padding?.top?.[0], padding?.top?.[1]),
		fluidValues('paddingRight', padding?.right?.[0], padding?.right?.[1]),
		fluidValues(
			'paddingBottom',
			padding?.bottom?.[0],
			padding?.bottom?.[1]
		),
		fluidValues('paddingLeft', padding?.left?.[0], padding?.left?.[1])
	);

/**
 * Fluid Margin
 * @param {Object} margin - styles values
 *
 * Example:
 * fluidMargin(styles.margin)
 */
export const fluidMargin = (margin) =>
	cssObj(
		fluidValues('marginTop', margin?.top?.[0], margin?.top?.[1]),
		fluidValues('marginRight', margin?.right?.[0], margin?.right?.[1]),
		fluidValues('marginBottom', margin?.bottom?.[0], margin?.bottom?.[1]),
		fluidValues('marginLeft', margin?.left?.[0], margin?.left?.[1])
	);

/**
 * Fluid Border width
 * @param {Object} width - styles values
 *
 * Example:
 * fluidBorderWidth(styles.border.width)
 */
export const fluidBorderWidth = (width) =>
	cssObj(
		fluidValues('borderTopWidth', width?.top?.[0], width?.top?.[1]),
		fluidValues('borderRightWidth', width?.right?.[0], width?.right?.[1]),
		fluidValues(
			'borderBottomWidth',
			width?.bottom?.[0],
			width?.bottom?.[1]
		),
		fluidValues('borderLeftWidth', width?.left?.[0], width?.left?.[1])
	);

/**
 * Fluid Border Radius
 * @param {Object} radius - styles values
 *
 * Example:
 * fluidBorderRadius(styles.border.radius)
 */
export const fluidBorderRadius = (radius) =>
	cssObj(
		fluidValues(
			'borderTopLeftRadius',
			radius?.topLeft?.[0],
			radius?.topLeft?.[1]
		),
		fluidValues(
			'borderTopRightRadius',
			radius?.topRight?.[0],
			radius?.topRight?.[1]
		),
		fluidValues(
			'borderBottomRightRadius',
			radius?.bottomRight?.[0],
			radius?.bottomRight?.[1]
		),
		fluidValues(
			'borderBottomLeftRadius',
			radius?.bottomLeft?.[0],
			radius?.bottomLeft?.[1]
		)
	);

/**
 * List Styles
 * @param {Object} styles - margialues
 *
 * Example:
 * listStyles(styles.list)
 */
export const listStyles = (styles) =>
	cssObj(
		resetFirstLastMargins,
		fontStyle({
			...styles?.text?.typography,
			textAlign: styles?.text?.textAlignment,
			color: styles?.text?.color,
		}),
		{
			padding: '0px !important',
			listStyle: 'none !important',

			'ul&': {
				'> li::before': cssObj(ulMarker, {
					borderLeft: `6px solid ${styles?.ul?.markerColor}`,
				}),
			},

			'ol&': {
				counterReset: 'li',

				'> li': {
					paddingLeft: '1.3em',

					'&:nth-child(n + 10)': {
						paddingLeft: '1.9em',
					},

					'&::before': cssObj(olMarker, {
						content: "counter(li) '.'",
						color: styles?.ol?.markerColor,
					}),
				},
			},

			s: {
				textDecoration: 'none',
			},

			code: {
				fontFamily: 'inherit',
				margin: 'initial',
				padding: 'initial',
			},

			'sub, sup': {
				verticalAlign: 'baseline',
				fontSize: 'inherit',
			},

			img: {
				display: 'none',
			},

			/* stylelint-disable-next-line */
			'ul, ol': cssObj(resetFirstLastMargins, {
				listStyle: 'none !important',
				padding: '0px !important',
				marginTop: styles?.text?.typography?.margin?.top,
				marginBottom: styles?.text?.typography?.margin?.bottom,
			}),

			/* stylelint-disable-next-line */
			li: cssObj(resetLastMarginBottom, {
				position: 'relative',
				paddingLeft: '1.3em',
				marginTop: `${styles?.text?.typography?.margin?.top} !important`,
				marginBottom: `${styles?.text?.typography?.margin?.bottom} !important`,

				'&::before': {
					content: '""',
					position: 'absolute',
					left: '0px',
				},
			}),

			/* Child Ordered List - letter bullet */
			ol: {
				counterReset: 'li',

				'> li::before': cssObj(olMarker, {
					content: "counter(li, lower-alpha) '.'",
					fontWeight: fontWeights.bold,
					color: styles?.ol?.subMarkerColor,
				}),
			},

			/* Child Unordered List - gray triangle */
			ul: {
				'> li::before': cssObj(ulMarker, {
					borderLeft: `6px solid ${styles?.ul?.subMarkerColor}`,
				}),
			},
		}
	);

/**
 * Underline with background-size
 * @param {Color} color
 * @param {Number} width
 * @param {Number} height
 *
 * Example:
 * underline(white, 100%, 1px);
 */
export const underline = (color, width, height) =>
	cssObj({
		display: 'inline',
		position: 'relative',
		backgroundImage: `linear-gradient(${color}, ${color})`,
		backgroundPosition: 'left bottom',
		backgroundRepeat: 'no-repeat',
		backgroundSize: `${width} ${height}`,
	});

/**
 * Gap polyfill
 * @param {Array} columnGap
 * @param {Array} rowGap
 * @param {Array} marginTop
 * @param {Array} marginLeft
 *
 * Example:
 * flexGapPolyfill([10, 20], [10, 20], [20, 40], [0, 0]);
 */
export const flexGapPolyfill = (
	columnGap = [0, 0],
	rowGap = [0, 0],
	marginTop = [0, 0],
	marginLeft = [0, 0]
) => {
	rowGap = rowGap || columnGap;

	const marginLeftFinal = [
		-1 * columnGap[0] + marginLeft[0],
		-1 * columnGap[1] + marginLeft[1],
	];
	const marginTopFinal = [
		-1 * rowGap[0] + marginTop[0],
		-1 * rowGap[1] + marginTop[1],
	];

	return cssObj(
		fluidValues('columnGap', columnGap[0], columnGap[1]),
		fluidValues('rowGap', rowGap[0], rowGap[1]),
		{
			marginLeft: 0,

			'& > *': {
				margin: '0px !important',
			},
			// Flexbox gap support: https://caniuse.com/flexbox-gap
			// Solution found: Target Safari on iOS devices
			// Can be improved if we find a way to target only browsers that don't support gap with flexbox
			/* CSS specific to Safari on iOS devices */
			'@supports (-webkit-touch-callout: none) and (not(translate: none))':
				cssObj(
					fluidValues(
						'marginTop',
						marginTopFinal[0],
						marginTopFinal[1],
						true
					),
					fluidValues(
						'marginLeft',
						marginLeftFinal[0],
						marginLeftFinal[1],
						true
					),
					{
						columnGap: 0,
						rowGap: 0,

						'& > *': cssObj(
							fluidValues(
								'marginTop',
								rowGap[0],
								rowGap[1],
								true
							),
							fluidValues(
								'marginLeft',
								columnGap[0],
								columnGap[1],
								true
							)
						),
					}
				),
		}
	);
};
